import React, { useState, useEffect, useCallback } from 'react';
import { Grid2, Box, IconButton, Typography, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { debounce } from 'lodash';

import useCommon from '../../hooks/useCommon';
import DateRangePicker from '../Common/DateRangePicker';
import ReactMuiTableListView from '../Common/TableComponent/TableComponent';
import SearchBox from '../Common/SearchBox';
import ReactMuiTableColumnHeaderTextEllipsis from '../Common/ReactMuiTableColumnHeaderTextEllipsis';
import { ReactComponent as DownloadIcon } from '../../assets/DownloadIcon.svg';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import { CommonTableCell, DateTimeCell } from '../Common/CommonTableCell';

// Define table columns similar to AccessLogs
const columns = [
  {
    heading: 'ACTION USERNAME',
    accessor: 'action_user_name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 170,
  },
  {
    heading: 'ACTION USERID',
    accessor: 'action_user_id',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 150,
    Cell: CommonTableCell,
  },
  {
    heading: 'TIMESTAMP',
    accessor: '@timestamp',
    Cell: DateTimeCell,
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 200,
  },
  {
    heading: 'IP ADDRESS',
    accessor: 'ip_address',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 130,
    Cell: CommonTableCell,
  },
  {
    heading: 'EVENT NAME',
    accessor: 'event_name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 150,
  },
  {
    heading: 'REQUEST METHOD',
    accessor: 'request_method',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 170,
  },
  {
    heading: 'ACTION',
    accessor: 'action',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 150,
  },
  {
    heading: 'RESOURCE NAME',
    accessor: 'resource_name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 150,
  },
  {
    heading: 'EVENT DESCRIPTION',
    accessor: 'event_description',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    Cell: CommonTableCell,
    width: 200,
  },
  {
    heading: 'STATUS',
    accessor: 'status',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 100,
    Cell: CommonTableCell,
  },
];

const AuditLogs = ({
  fetchAuditLogs,
  auditLogsList,
  auditLogsPageCount,
  totalAuditLogsCount,
  DownloadLogs,
  orgName,
}) => {
  const theme = useTheme();
  const {
    dispatch,
    common,
    setAuditLogsCreatedOnFilter,
    setAuditLogsNameSearch,
  } = useCommon();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(5);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const downloadLogs = useCallback(() => {
    const parameters = {
      type: 'audit',
      start_date: common.auditLogsCreatedOnFilter?.fromDate,
      end_date: common.auditLogsCreatedOnFilter?.toDate,
      text: common.auditLogsNameSearch || '',
    };
    DownloadLogs(orgName, parameters).then((res) => {
      window.open(res.data, '_blank', 'noopener', 'noreferrer');
    });
  }, [
    orgName,
    common.auditLogsCreatedOnFilter,
    common.auditLogsNameSearch,
    DownloadLogs,
  ]);

  const fetchAuditLogsListWrapper = useCallback(() => {
    if (currentPageIndex >= 0 && currentPageSize) {
      fetchAuditLogs(
        orgName,
        currentPageIndex,
        currentPageSize,
        common.auditLogsNameSearch,
        common.auditLogsCreatedOnFilter?.fromDate,
        common.auditLogsCreatedOnFilter?.toDate,
        sortField,
        sortOrder
      );
    }
  }, [
    common.auditLogsNameSearch,
    common.auditLogsCreatedOnFilter?.fromDate,
    common.auditLogsCreatedOnFilter?.toDate,
    currentPageIndex,
    currentPageSize,
    sortField,
    sortOrder,
  ]);

  const handleSearch = useCallback(
    debounce(
      (
        globalFilter,
        pageIndex,
        pageSize,
        sortfield,
        sortorder,
        fromDate,
        toDate
      ) => {
        fetchAuditLogs(
          orgName,
          pageIndex,
          pageSize,
          globalFilter,
          fromDate,
          toDate,
          sortfield,
          sortorder
        );
      },
      1200
    ),
    []
  );
  const cancelDebouncedCall = () => handleSearch.cancel();

  // date range filter handler
  const handleDateRangePickerOnChange = useCallback(
    (fromDate, toDate) => {
      // eslint-disable-next-line no-underscore-dangle
      if (fromDate?._isValid && toDate?._isValid) {
        dispatch(
          setAuditLogsCreatedOnFilter({
            fromDate: fromDate.format('YYYY/MM/DD'),
            toDate: toDate.format('YYYY/MM/DD'),
          })
        );
      } else if (fromDate === null && toDate === null) {
        dispatch(setAuditLogsCreatedOnFilter({ fromDate: null, toDate: null }));
      }
    },
    [dispatch]
  );

  // Reload audit logs when pagination, search, filters, or sorting change
  useEffect(() => {
    if (currentPageIndex >= 0 && currentPageSize) {
      if (common?.auditLogsNameSearch?.length > 0) {
        handleSearch(
          common?.auditLogsNameSearch,
          currentPageIndex,
          currentPageSize,
          sortField,
          sortOrder,
          common?.auditLogsCreatedOnFilter?.fromDate,
          common?.auditLogsCreatedOnFilter?.toDate
        );
      } else {
        cancelDebouncedCall();
        fetchAuditLogs(
          orgName,
          currentPageIndex,
          currentPageSize,
          common.auditLogsNameSearch,
          common.auditLogsCreatedOnFilter?.fromDate,
          common.auditLogsCreatedOnFilter?.toDate,
          sortField,
          sortOrder
        );
      }
    }
  }, [
    currentPageIndex,
    currentPageSize,
    common.auditLogsNameSearch,
    common.auditLogsCreatedOnFilter?.fromDate,
    common.auditLogsCreatedOnFilter?.toDate,
    sortField,
    sortOrder,
  ]);

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndexData - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndexData)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setCurrentPageIndex(pageIndexData?.pageIndex);
      setCurrentPageSize(pageIndexData?.pageSize);
    },
    [setCurrentPageIndex, setCurrentPageSize]
  );

  const handleSortChange = useCallback(
    (sortDetails) => {
      if (sortDetails?.length) {
        const [{ id, desc }] = sortDetails;
        setSortOrder(desc ? 'desc' : 'asc');
        setSortField(id);
      } else {
        setSortOrder('');
        setSortField('');
      }
    },
    [setSortField, setSortOrder]
  );

  const handleAuditLogsNameSearch = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchTerm(value);
      if (value) {
        dispatch(setAuditLogsNameSearch(value));
      } else {
        dispatch(setAuditLogsNameSearch(''));
      }
    },
    [dispatch]
  );

  return (
    <Grid2 container spacing={2} mt={2} mb={1}>
      <Grid2 size={{ xs: 12 }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <IconButton
              id="refresh-btn"
              color="primary"
              onClick={fetchAuditLogsListWrapper}
              aria-label="Refresh"
              title="Refresh audit logs"
            >
              <RefreshIcon color="primary" />
            </IconButton>
            <Box width="200px" ml={1}>
              <SearchBox
                value={common.auditLogsNameSearch}
                onChange={handleAuditLogsNameSearch}
                placeholder="Search"
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box width={350}>
              <DateRangePicker
                format="DD/MM/YYYY"
                initialFromDate={common?.auditLogsCreatedOnFilter?.fromDate}
                initialToDate={common?.auditLogsCreatedOnFilter?.toDate}
                onChange={handleDateRangePickerOnChange}
                TextFieldProps={{
                  size: 'small',
                  InputProps: {
                    sx: {
                      input: {
                        paddingTop: '15px',
                      },
                    },
                  },
                }}
                isFutureDatesDisabled={true}
              />
            </Box>
            <IconButton
              title="Download CSV"
              color="primary"
              onClick={downloadLogs}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        {auditLogsList?.length > 0 ? (
          <ReactMuiTableListView
            data={auditLogsList}
            columns={columns}
            onTableStateChange={onTableStateChangeMethod}
            getHeaderProps={() => ({
              style: { display: 'flex', alignItems: 'center' },
            })}
            getRowProps={() => ({
              style: { position: 'relative' },
            })}
            enableRowSelection={true}
            pageCount={auditLogsPageCount}
            dataLength={Number(totalAuditLogsCount)}
            enablePagination={true}
            initialPageSize={5}
            rowsPerPageOptions={[5, 10, 15, 20]}
            rowLevelRightClickMenus={[]}
            enableManualFilter={true}
            enableControlledPagination={true}
            showPaginationAction={false}
            skipPageReset={true}
            searchTerm={searchTerm}
            enableManualSort
            onSortChange={handleSortChange}
          />
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            overflow="auto"
            minHeight="300px"
            height="calc(100vh - 438px)"
          >
            {searchTerm ? (
              <NoDataImage style={{ color: theme.palette.primary.main }} />
            ) : (
              <NoDataCreated style={{ color: theme.palette.primary.main }} />
            )}
            <Typography
              variant="subtitle1"
              textAlign="center"
              sx={{ pt: '10px' }}
            >
              {searchTerm
                ? 'No matching result found.'
                : 'No audit logs generated.'}
            </Typography>
          </Box>
        )}
      </Grid2>
    </Grid2>
  );
};

export default AuditLogs;
